import { CORVIN } from '@somnhold/content';
import { blockingAtCell, cellAtWorld } from '../../grid.ts';
import { cosA, iatan2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { knockBack } from '../enemies.ts';
import { at, by, held, hurt, inCone, reachable, standing, turn, wallLike, type Kit } from './kit.ts';

/*
 * Ser Corvin, the Knight of the Gate (warrior): a challenge that marks a hero (he hunts it, and its blows on others
 * make his next one land double), a charge along a line that breaks the wall it meets, and the Night of the Gate — a
 * cone of shadow that throws heroes back and breaks walls and gates. Blows from in front of him land at a fraction.
 */

// cds: challenge, charge. busy: the charge's steps (vx, vy each), tgts: the heroes it struck.
const CHALLENGE = 0;
const CHARGE = 1;

/** His blow on a hero: double on a marked hero that turned its back on his challenge. */
export const corvinBlow = (h: Hero, dmg: number): number => {
  const d = by(h, dmg, CORVIN.blowMaxHpBp);
  if ((h.unMark ?? 0) <= 0 || !h.unBroke) return d;
  h.unBroke = false;
  h.unMark = 0;
  return mulBp(d, CORVIN.challenge.doubleBp);
};

/** Marks the nearest heroes he can reach (the one he hunts first, if it is near), and makes for the first. */
const challenge = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const c = CORVIN.challenge;
  const marks = e.phase >= 1 ? 2 : 1;
  const free = s.heroes.filter((h) => standing(h) && (h.unImmune ?? 0) <= s.tick && withinRange(e.x, e.y, h.x, h.y, c.range) && reachable(s, e, h));
  if (!free.length) {
    u.cds[CHALLENGE] = 6;
    return;
  }
  free.sort((a, b) => {
    const ha = a.pid === u.hunt ? 0 : 1;
    const hb = b.pid === u.hunt ? 0 : 1;
    if (ha !== hb) return ha - hb;
    const da = (a.x - e.x) * (a.x - e.x) + (a.y - e.y) * (a.y - e.y);
    const db = (b.x - e.x) * (b.x - e.x) + (b.y - e.y) * (b.y - e.y);
    return da - db || a.id - b.id;
  });
  const picked = free.slice(0, marks);
  for (const h of picked) {
    h.unMark = c.ticks;
    h.unBroke = false;
    held(s, h);
    ev(s, { t: 'beam', x: e.x, y: e.y, tx: h.x, ty: h.y, kind: 'corvinChallenge' });
  }
  const first = picked[0] as Hero;
  e.taunter = first.id;
  e.tauntTicks = c.ticks;
  u.cds[CHALLENGE] = c.every;
  ev(s, { t: 'skill', hero: e.id, skill: 'corvinChallenge', x: first.x, y: first.y });
};

/** Charges at a hero far enough and in reach (the marked one first). */
const charge = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const c = CORVIN.charge;
  let tgt: Hero | null = null;
  let best = 0x7fffffff;
  for (const h of s.heroes) {
    if (!standing(h) || !reachable(s, e, h)) continue;
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = isqrt(dx * dx + dy * dy);
    if (d < c.minRange || d > c.range) continue;
    const score = (h.id === e.taunter ? 0 : 0x100000) + d;
    if (score < best) {
      best = score;
      tgt = h;
    }
  }
  if (!tgt) return false;
  const dx = tgt.x - e.x;
  const dy = tgt.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  // Past the hero a little: the line goes through it.
  const far = imin(c.range, d + 600);
  u.busy = imax(1, idiv(far + c.step - 1, c.step));
  u.vx = idiv(dx * c.step, d);
  u.vy = idiv(dy * c.step, d);
  u.tgts = [];
  u.cds[CHARGE] = c.every;
  e.facing = iatan2(dy, dx);
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: e.x + idiv(dx * far, d), ty: e.y + idiv(dy * far, d), kind: 'corvinCharge' });
  ev(s, { t: 'skill', hero: e.id, skill: 'corvinCharge', x: e.x, y: e.y });
  return true;
};

const dashStep = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const c = CORVIN.charge;
  const nx = e.x + u.vx;
  const ny = e.y + u.vy;
  const cell = cellAtWorld(s, nx, ny);
  const wall = blockingAtCell(s, cell);
  if (cell < 0 || (wall && !wall.destroyed)) {
    // A wall or a gate on the line: it takes the brunt, and the charge ends against it.
    if (wall && !wall.destroyed) damageStructure(s, wall, mulBp(mulBp(c.damage, u.dmgBp), c.wallBp), e);
    u.busy = 0;
    e.next = -1;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'corvinImpact' });
    return true;
  }
  e.x = nx;
  e.y = ny;
  e.anim = 'walk';
  for (const h of s.heroes) {
    if (!standing(h) || u.tgts.includes(h.id) || !withinRange(e.x, e.y, h.x, h.y, c.width)) continue;
    u.tgts.push(h.id);
    hurt(s, e, u, h, c.damage, c.maxHpBp);
    if (standing(h) && (h.frenzy ?? 0) <= 0) h.stun = imax(h.stun, c.stun);
  }
  if (--u.busy === 0) {
    e.next = -1;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'corvinImpact' });
  }
  return true;
};

export const corvin: Kit = {
  init: (_s, _e, u) => {
    u.cds = [0, idiv(CORVIN.charge.every, 2)];
  },
  busy: dashStep,
  act: (s, e, u) => {
    if (u.cds[CHALLENGE] === 0) challenge(s, e, u);
    return u.cds[CHARGE] === 0 && charge(s, e, u);
  },
  // The Night toward the most heroes (else the way he walks); with no hero near, only on a wall in front of him.
  aim: (s, e, u) => {
    const n = CORVIN.night;
    let a = e.facing;
    let most = 0;
    for (const h of s.heroes) {
      if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, n.radius)) continue;
      const ha = iatan2(h.y - e.y, h.x - e.x);
      let k = 0;
      for (const o of s.heroes) if (standing(o) && inCone(e, ha, o.x, o.y, n.radius, n.half)) k++;
      if (k > most) {
        most = k;
        a = ha;
      }
    }
    if (most === 0 && !s.structures.some((st) => !st.destroyed && wallLike(st.type) && st.maxHp > 0 && inCone(e, a, at(st).x, at(st).y, n.radius, n.half))) return false;
    u.castA = a;
    e.facing = a;
    ev(s, { t: 'skill', hero: e.id, skill: 'corvinNight', x: e.x, y: e.y });
    return true;
  },
  land: (s, e, u) => {
    const n = CORVIN.night;
    const a = u.castA;
    for (const h of s.heroes) {
      if (!standing(h) || !inCone(e, a, h.x, h.y, n.radius, n.half)) continue;
      hurt(s, e, u, h, n.damage, n.maxHpBp, 'shadow');
      if (standing(h)) knockBack(s, e, h, { far: n.knock, ticks: 6, immune: 0 });
    }
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0) continue;
      const c = at(st);
      if (!inCone(e, a, c.x, c.y, n.radius, n.half)) continue;
      // Walls and gates break; everything else (towers, the belfry, the forge…) only takes a share.
      if (wallLike(st.type)) damageStructure(s, st, st.maxHp * 4, e, 'shadow');
      else damageStructure(s, st, mulBp(st.maxHp, n.towerBp), e, 'shadow');
    }
    // In his last phase the ground stays dark and hurts whoever stands on it.
    if (e.phase >= 2) {
      const sh = n.shadow;
      for (let i = 1; i <= 3; i++) {
        const r = idiv(n.radius * i, 4);
        const x = e.x + idiv(r * cosA(a), 10000);
        const y = e.y + idiv(r * sinA(a), 10000);
        s.zones.push({ id: s.nextId++, kind: 'nightfall', x, y, radius: sh.radius, cells: [], ticks: sh.ticks, every: 6, damage: mulBp(sh.damage, u.dmgBp), slowBp: 0, pid: '' });
      }
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: n.radius, kind: 'corvinNight' });
  },
  // The gate doesn't fall: blows from in front of him land at a fraction.
  takes: (_s, e, u, dmg, x, y) => {
    if (e.stun > 0 || u.cast > 0 || x === undefined || y === undefined || (x === e.x && y === e.y)) return dmg;
    const d = turn(e.facing, iatan2(y - e.y, x - e.x));
    return d <= CORVIN.frontHalf && d >= -CORVIN.frontHalf ? mulBp(dmg, CORVIN.frontTakenBp) : dmg;
  },
  // His blow lands on `h` and sweeps every other hero close in front of him.
  strike: (s, e, h, dmg) => {
    damageHero(s, h, corvinBlow(h, dmg), e, undefined, true);
    const c = CORVIN.cleave;
    for (const o of s.heroes) {
      if (o === h || !standing(o) || !inCone(e, e.facing, o.x, o.y, c.range, c.half)) continue;
      damageHero(s, o, corvinBlow(o, dmg), e, undefined, true);
    }
  },
};

